import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Gavel,
  ExternalLink,
  Scale,
  Briefcase,
  Home,
  Shield,
  Landmark,
  Heart,
  MessageCircle,
  BookOpen,
} from "lucide-react";

const LOGO_ICON = "https://customer-assets.emergentagent.com/job_direito-trabalho-sp/artifacts/8b5v8061_IconOnly_Transparent_NoBuffer.png";
const WHATSAPP_LINK = "https://wa.me/5511917611575?text=Olá! Gostaria de uma consulta jurídica.";

const tribunais = [
  {
    sigla: "STF",
    nome: "Supremo Tribunal Federal",
    descricao: "Órgão máximo do Poder Judiciário brasileiro, responsável pela guarda da Constituição Federal.",
    url: "https://portal.stf.jus.br/",
    urlJurisprudencia: "https://portal.stf.jus.br/jurisprudencia/",
    areas: ["Constitucional", "Repercussão Geral"],
    icon: <Scale className="w-6 h-6" />,
    color: "bg-red-100 text-red-700",
  },
  {
    sigla: "STJ",
    nome: "Superior Tribunal de Justiça",
    descricao: "Responsável por uniformizar a interpretação da lei federal em todo o Brasil.",
    url: "https://www.stj.jus.br/",
    urlJurisprudencia: "https://scon.stj.jus.br/SCON/",
    areas: ["Cível", "Tributário", "Família", "Previdenciário"],
    icon: <Landmark className="w-6 h-6" />,
    color: "bg-blue-100 text-blue-700",
  },
  {
    sigla: "TST",
    nome: "Tribunal Superior do Trabalho",
    descricao: "Instância máxima da Justiça do Trabalho, uniformiza a jurisprudência trabalhista nacional.",
    url: "https://www.tst.jus.br/",
    urlJurisprudencia: "https://jurisprudencia.tst.jus.br/",
    areas: ["Trabalhista"],
    icon: <Briefcase className="w-6 h-6" />,
    color: "bg-green-100 text-green-700",
  },
  {
    sigla: "TRT-2",
    nome: "Tribunal Regional do Trabalho da 2ª Região",
    descricao: "Tribunal trabalhista de São Paulo, uma das maiores cortes trabalhistas do país.",
    url: "https://ww2.trt2.jus.br/",
    urlJurisprudencia: "https://ww2.trt2.jus.br/jurisprudencia/",
    areas: ["Trabalhista"],
    icon: <Briefcase className="w-6 h-6" />,
    color: "bg-emerald-100 text-emerald-700",
  },
];

const areasInfo = [
  { icon: <Briefcase className="w-5 h-5" />, area: "Direito do Trabalho", tribunais: "TST, TRT-2", descricao: "Verbas rescisórias, horas extras, adicional de insalubridade, vínculo empregatício" },
  { icon: <Scale className="w-5 h-5" />, area: "Direito Cível", tribunais: "STJ", descricao: "Responsabilidade civil, danos morais, contratos, cobranças" },
  { icon: <Shield className="w-5 h-5" />, area: "Direito Previdenciário", tribunais: "STJ, STF", descricao: "Aposentadorias, auxílios, BPC/LOAS, revisão de benefícios" },
  { icon: <Landmark className="w-5 h-5" />, area: "Direito Tributário", tribunais: "STF, STJ", descricao: "ICMS, PIS/COFINS, execuções fiscais, planejamento tributário" },
  { icon: <Heart className="w-5 h-5" />, area: "Direito de Família", tribunais: "STJ", descricao: "Guarda compartilhada, pensão alimentícia, divórcio, partilha" },
  { icon: <Home className="w-5 h-5" />, area: "Direito Imobiliário", tribunais: "STJ", descricao: "Compra e venda, usucapião, locação, condomínio" },
];

const JulgadosPage = () => {
  return (
    <>
      <Helmet>
        <title>Jurisprudência | Helio Lopes Leal Advocacia</title>
        <meta name="description" content="Consulte jurisprudência dos principais tribunais brasileiros. Links diretos para STF, STJ, TST e TRT-2." />
      </Helmet>

      <div>
        {/* Hero */}
        <section className="pt-32 pb-16 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
          <div className="absolute right-0 top-1/2 -translate-y-1/2 opacity-[0.03] pointer-events-none">
            <img src={LOGO_ICON} alt="" className="w-[500px] h-auto" style={{ filter: "brightness(0)" }} />
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <p className="text-blue-900 font-medium tracking-wider text-sm mb-4">JURISPRUDÊNCIA</p>
            <h1
              className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Consulta de <span className="text-blue-900">Jurisprudência</span>
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl">
              Acesse diretamente os portais oficiais dos principais tribunais brasileiros 
              para consultar decisões e jurisprudência atualizada.
            </p>
          </div>
        </section>

        {/* Tribunais */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 
                className="text-2xl md:text-3xl font-bold text-gray-900 mb-4"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Tribunais Superiores e Regionais
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Clique nos links abaixo para acessar os portais oficiais de jurisprudência
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {tribunais.map((tribunal) => (
                <div 
                  key={tribunal.sigla}
                  className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all"
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${tribunal.color}`}>
                      {tribunal.icon}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-900">{tribunal.sigla}</h3>
                      <p className="text-sm text-gray-600">{tribunal.nome}</p>
                    </div>
                  </div>
                  
                  <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                    {tribunal.descricao}
                  </p>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {tribunal.areas.map((area) => (
                      <span 
                        key={area}
                        className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded"
                      >
                        {area}
                      </span>
                    ))}
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <a
                      href={tribunal.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-sm font-medium transition-all"
                    >
                      Portal {tribunal.sigla}
                      <ExternalLink className="w-4 h-4" />
                    </a>
                    <a
                      href={tribunal.urlJurisprudencia}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-900 hover:bg-blue-800 text-white rounded text-sm font-medium transition-all"
                    >
                      Jurisprudência
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Áreas e Tribunais */}
        <section className="py-16 bg-gray-50 relative overflow-hidden">
          <div className="absolute left-0 bottom-0 opacity-[0.02] pointer-events-none">
            <img src={LOGO_ICON} alt="" className="w-[500px] h-auto" style={{ filter: "brightness(0)" }} />
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center mb-12">
              <BookOpen className="w-10 h-10 text-blue-900 mx-auto mb-4" />
              <h2 
                className="text-2xl md:text-3xl font-bold text-gray-900 mb-4"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Onde Pesquisar por Área
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Cada área do Direito possui tribunais específicos para consulta de jurisprudência
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {areasInfo.map((item, index) => (
                <div 
                  key={index}
                  className="bg-white p-6 rounded-lg border border-gray-100"
                >
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center text-blue-900 mb-4">
                    {item.icon}
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-1">{item.area}</h4>
                  <p className="text-xs text-blue-900 font-medium mb-2">Tribunais: {item.tribunais}</p>
                  <p className="text-gray-600 text-sm">{item.descricao}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Nota Importante */}
        <section className="py-12 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 max-w-4xl mx-auto">
              <div className="flex items-start gap-4">
                <Gavel className="w-8 h-8 text-amber-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Importância da Análise Profissional
                  </h3>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    A jurisprudência é uma ferramenta fundamental para compreender como os tribunais 
                    decidem casos semelhantes. No entanto, cada situação possui particularidades que 
                    exigem análise técnica especializada. Consulte sempre um advogado para entender 
                    como as decisões podem se aplicar ao seu caso específico.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-gray-900 relative overflow-hidden">
          <div className="absolute right-0 top-1/2 -translate-y-1/2 opacity-[0.05] pointer-events-none">
            <img src={LOGO_ICON} alt="" className="w-[400px] h-auto invert" />
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
            <h2 
              className="text-3xl font-bold text-white mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Precisa de Orientação Jurídica?
            </h2>
            <p className="text-gray-400 mb-8 max-w-xl mx-auto">
              Entre em contato e tire suas dúvidas sobre como a jurisprudência 
              pode impactar o seu caso específico.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded font-medium transition-all"
              >
                <MessageCircle className="w-5 h-5" />
                Falar com Advogado
              </a>
              <Link
                to="/contato"
                className="inline-flex items-center justify-center gap-2 border-2 border-gray-600 hover:border-white text-gray-300 hover:text-white px-8 py-4 rounded font-medium transition-all"
              >
                Outras Formas de Contato
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default JulgadosPage;
